// Libs
import React, { useState, useContext, useEffect, useRef } from "react";
import { useObserver, KeyValuePair, FieldType, Else, EditableInput, TypographyX } from "@shoothill/core";
import SignaturePad from "react-signature-pad-wrapper";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { FormLabel, Box, Typography, Checkbox, FormControlLabel, Radio, FormControl, Button, Select, InputAdornment, TextField, useMediaQuery } from "@material-ui/core";
import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import styled from "styled-components";
import ReactHtmlParser from "react-html-parser";

// App
import { StoresContext, Stores } from "Globals/Stores";

// Models
import { HomeOwnerStatusEnum } from "Globals/Models/HomeOwnerStatusEnum";
import { PropertyDetailsModel } from "Globals/Models/Domain/FormSections/PropertyDetails/PropertyDetailsModel";

// ViewModels
import ApplicationFormViewModel from "Globals/ViewModels/ApplicationFormViewModel";
import ApplicantViewModel from "Globals/ViewModels/ApplicantViewModel";
import PropertyDetailsViewModel from "Globals/ViewModels/FormSections/PropertyDetails/PropertyDetailsViewModel";

// Views
import { CurrencyInput } from "Globals/Views/CurrencyInput";
import { CustomYesNoRadio } from "Globals/Views/CustomYesNoRadio";
import { CustomDatePicker } from "Globals/Views/CustomDatePicker";

// Styles
import { theme } from "Globals/Styles/AppTheme";
import { CustomTwoSelects } from "Globals/Styles/CustomTwoSelects";
import { Row } from "Globals/Styles/LayoutStyles";
import { CheckboxSVG } from "Globals/Views/IconSVGs/CheckboxSVG";
import { CheckboxCheckedSVG } from "Globals/Views/IconSVGs/CheckboxCheckedSVG";

export const StyledButton = styled(Button)`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 8px 31px;
    flex: 1;
    background-color: ${theme.palette.common.black};
    color: ${theme.palette.common.white};
    &.Mui-disabled {
        color: ${theme.palette.common.white};
        opacity: 0.5;
    }
    :first-of-type {
        margin-right: 30px;
        background-color: ${theme.palette.hightownBlue.light};
        color: ${theme.palette.common.white};
    }
    :hover {
        background-color: ${theme.palette.hightownBlue.light};
    }
`;

interface IProps {
    applicant: ApplicantViewModel;
    applicationFormViewModel: ApplicationFormViewModel;
}

export const DeclarationView: React.FC<IProps> = (props: IProps) => {
    const storesContext = useContext<Stores>(StoresContext);
    const [viewModel] = useState(props.applicant.declarationViewModel);
    const signatureRef = useRef<SignaturePad>() as React.RefObject<SignaturePad>;
    const isMobile: boolean = useMediaQuery("screen and (max-width: 767px)") ? true : false;
    useEffect(() => {
        viewModel.loadSignature();
    }, []);

    return useObserver(() => (
        <div onMouseUp={() => viewModel.restartTimer()}>
            {viewModel.DevTool_ShowCanProceed && <h5 style={{ color: "red" }}>Can Proceed: {viewModel.canProceed ? "Yes" : "No"}</h5>}
            <Row>
                <Typography variant={"body2"} dangerouslySetInnerHTML={{ __html: viewModel.getEditableCopyByRouteAndPlaceholderNumber("/declaration", 1) }}></Typography>
            </Row>
            <Row>
                <Typography variant={"body2"} dangerouslySetInnerHTML={{ __html: viewModel.getEditableCopyByRouteAndPlaceholderNumber("/declaration", 2) }}></Typography>
            </Row>
            <Row>
                <Typography variant={"body2"} dangerouslySetInnerHTML={{ __html: viewModel.getEditableCopyByRouteAndPlaceholderNumber("/declaration", 3) }}></Typography>
            </Row>
            <Row>
                <Typography variant={"body2"} dangerouslySetInnerHTML={{ __html: viewModel.getEditableCopyByRouteAndPlaceholderNumber("/declaration", 4) }}></Typography>
            </Row>
            <Row>
                <Typography variant={"body2"} dangerouslySetInnerHTML={{ __html: viewModel.getEditableCopyByRouteAndPlaceholderNumber("/declaration", 5) }}></Typography>
            </Row>
            <Row>
                <Typography variant={"body2"} dangerouslySetInnerHTML={{ __html: viewModel.getEditableCopyByRouteAndPlaceholderNumber("/declaration", 6) }}></Typography>
            </Row>
            <Row>
                <Typography variant={"body2"} dangerouslySetInnerHTML={{ __html: viewModel.getEditableCopyByRouteAndPlaceholderNumber("/declaration", 7) }}></Typography>
            </Row>
            <Row>
                <Typography variant={"body2"} dangerouslySetInnerHTML={{ __html: viewModel.getEditableCopyByRouteAndPlaceholderNumber("/declaration", 8) }}></Typography>
            </Row>
            <Row>
                <Typography variant={"body2"} dangerouslySetInnerHTML={{ __html: viewModel.getEditableCopyByRouteAndPlaceholderNumber("/declaration", 9) }}></Typography>
            </Row>

            <Row style={{ alignItems: "center", flexDirection: "row" }}>
                <Checkbox
                    checked={!viewModel.model.canContact}
                    onChange={(e) => {
                        viewModel.setValue("canContact", !e.target.checked);
                        viewModel.restartTimer();
                    }}
                    icon={<CheckboxSVG />}
                    checkedIcon={<CheckboxCheckedSVG />}
                    disabled={props.applicationFormViewModel.model.isSubmitted}
                />
                <Typography variant="body1" style={{ marginLeft: "30px" }}>
                    I/we consent to be contacted further
                </Typography>
            </Row>

            <Row>
                <FormControl component="fieldset">
                    <FormLabel component="legend" style={isMobile ? { marginBottom: "10px" } : { marginBottom: "5px" }}>
                        <span style={isMobile ? { fontSize: "16px", textTransform: "uppercase" } : {}}>Signature</span>
                    </FormLabel>
                    {viewModel.getError("signature") !== "" && (
                        <p style={{ color: theme.palette.error.main, position: "absolute", fontSize: "14px" }}>{viewModel.getError("signature")}</p>
                    )}

                    <>
                        <div style={{ maxHeight: "350px", maxWidth: "500px" }}>
                            {viewModel.signatureURL == "" ? (
                                <SignaturePad
                                    ref={signatureRef}
                                    options={{
                                        backgroundColor: "rgb(255,255,255)",
                                        onEnd: () => viewModel.handleSignatureChange(signatureRef!.current!.toDataURL()),
                                    }}
                                />
                            ) : (
                                <div style={isMobile ? { width: "100%" } : { maxHeight: "350px", maxWidth: "500px" }}>
                                    <img style={{ width: "100%", height: "100%" }} src={viewModel.signatureURL} />
                                </div>
                            )}
                        </div>
                        {viewModel.IsLoading ||
                            props.applicationFormViewModel.model.isSubmitted ||
                            (storesContext.Domain.SaveStore.isSaving == false && (
                                <Row style={{ marginTop: "0px" }}>
                                    <StyledButton
                                        onClick={() => viewModel.saveSignature()}
                                        disabled={viewModel.signatureURL != ""}
                                        style={isMobile ? { width: "100%", margin: "20px 0" } : {}}
                                    >
                                        Add
                                    </StyledButton>
                                    <StyledButton
                                        onClick={() => {
                                            signatureRef.current ? signatureRef.current.clear() : null;
                                            viewModel.handleSignatureChange("");
                                        }}
                                    >
                                        Clear
                                    </StyledButton>
                                </Row>
                            ))}
                    </>
                </FormControl>

                <CustomDatePicker
                    viewModel={viewModel}
                    label="Date"
                    placeholder={"Enter date"}
                    fieldName="signedDate"
                    value={viewModel.model.signedDate}
                    onChange={(date) => {
                        viewModel.restartTimer();
                        viewModel.setDate("signedDate", date);
                    }}
                    resetCallback={viewModel.restartTimer}
                    disabled={props.applicationFormViewModel.model.isSubmitted}
                />
            </Row>
        </div>
    ));
};
